import React, { useState, useEffect, useRef } from 'react';
import './GamingLoader.css';

/**
 * TRANSCEND CONSTELLATION LOADER
 * Elegant flowing particle animation - pure constellation effect
 * No intended shapes, just organic particle movement with subtle connections
 */

const PARTICLE_COUNT = 60;

/**
 * GamingLoader - Organic constellation particle animation
 * Particles drift and flow naturally with subtle connecting lines
 */
const GamingLoader = ({ message = 'Loading...', subtitle, progress }) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [particles, setParticles] = useState(() =>
    Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
      id: i,
      x: 30 + (Math.random() - 0.5) * 40,
      y: 30 + (Math.random() - 0.5) * 40,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      size: 0.5 + Math.random() * 0.8,
      opacity: 0.4 + Math.random() * 0.5,
      phase: Math.random() * Math.PI * 2
    }))
  );
  const animationRef = useRef(null);

  // Continuous organic particle animation
  useEffect(() => {
    let lastTime = performance.now();

    const animate = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      setParticles(prev => prev.map((particle) => {
        const center = 30;
        const maxDist = 24;

        // Gentle drift with slight attraction to center
        const distFromCenter = Math.sqrt(
          Math.pow(particle.x - center, 2) + Math.pow(particle.y - center, 2)
        );

        // Soft boundary - particles drift back when too far
        let ax = 0, ay = 0;
        if (distFromCenter > maxDist) {
          const pullStrength = (distFromCenter - maxDist) * 0.02;
          ax = -(particle.x - center) / distFromCenter * pullStrength;
          ay = -(particle.y - center) / distFromCenter * pullStrength;
        }

        // Add gentle wandering motion
        const wanderX = Math.sin(currentTime * 0.0008 + particle.phase) * 0.015;
        const wanderY = Math.cos(currentTime * 0.0006 + particle.phase * 1.3) * 0.015;

        // Update velocity with damping
        let newVx = (particle.vx + ax + wanderX) * 0.995;
        let newVy = (particle.vy + ay + wanderY) * 0.995;

        // Clamp velocity
        const maxSpeed = 0.8;
        const speed = Math.sqrt(newVx * newVx + newVy * newVy);
        if (speed > maxSpeed) {
          newVx = (newVx / speed) * maxSpeed;
          newVy = (newVy / speed) * maxSpeed;
        }

        // Update position
        let newX = particle.x + newVx;
        let newY = particle.y + newVy;

        // Soft size pulsing
        const sizePulse = 1 + Math.sin(currentTime * 0.002 + particle.phase) * 0.15;

        // Gentle opacity breathing
        const opacityPulse = 0.5 + Math.sin(currentTime * 0.0015 + particle.phase * 0.7) * 0.3;

        return {
          ...particle,
          x: newX,
          y: newY,
          vx: newVx,
          vy: newVy,
          currentSize: particle.size * sizePulse,
          currentOpacity: particle.opacity * opacityPulse
        };
      }));

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Handle progress
  useEffect(() => {
    if (progress !== undefined) {
      setDisplayProgress(progress);
    } else {
      const interval = setInterval(() => {
        setDisplayProgress(prev => {
          if (prev >= 95) return prev;
          return prev + Math.random() * 8;
        });
      }, 350);
      return () => clearInterval(interval);
    }
  }, [progress]);

  // Generate constellation connections between nearby particles
  const connections = [];
  const connectionDistance = 12;
  for (let i = 0; i < particles.length; i++) {
    for (let j = i + 1; j < particles.length; j++) {
      const dist = Math.hypot(
        particles[j].x - particles[i].x,
        particles[j].y - particles[i].y
      );
      if (dist < connectionDistance) {
        const opacity = (1 - dist / connectionDistance) * 0.25;
        connections.push({
          key: `${i}-${j}`,
          x1: particles[i].x,
          y1: particles[i].y,
          x2: particles[j].x,
          y2: particles[j].y,
          opacity
        });
      }
    }
  }

  return (
    <div className="gaming-loader-container">
      {/* Constellation particle animation */}
      <div className="morphing-loader-wrapper">
        <svg viewBox="0 0 60 60" className="morphing-loader-svg constellation">
          <defs>
            <linearGradient id="loaderBgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#2C3E50" />
              <stop offset="100%" stopColor="#34495e" />
            </linearGradient>
            <filter id="loaderParticleGlow">
              <feGaussianBlur stdDeviation="0.6" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <radialGradient id="loaderCenterGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="white" stopOpacity="0.2" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Dark background circle */}
          <circle cx="30" cy="30" r="28" fill="url(#loaderBgGradient)" />

          {/* Subtle center glow */}
          <circle cx="30" cy="30" r="20" fill="url(#loaderCenterGlow)" />

          {/* Constellation connection lines - white */}
          <g className="particle-connections">
            {connections.map((conn) => (
              <line
                key={conn.key}
                x1={conn.x1}
                y1={conn.y1}
                x2={conn.x2}
                y2={conn.y2}
                stroke="white"
                strokeWidth="0.4"
                opacity={conn.opacity}
              />
            ))}
          </g>

          {/* Flowing particles - white */}
          <g className="particle-field">
            {particles.map((p) => (
              <circle
                key={p.id}
                cx={p.x}
                cy={p.y}
                r={p.currentSize || p.size}
                fill="white"
                opacity={p.currentOpacity || p.opacity}
                filter="url(#loaderParticleGlow)"
              />
            ))}
          </g>
        </svg>
      </div>

      <div className="gaming-loader-text">{message}</div>

      {/* Terminal charging bar */}
      <div className="terminal-progress">
        <div className="terminal-bar-container">
          <div className="terminal-bar">
            <div
              className="terminal-bar-fill"
              style={{ width: `${Math.min(displayProgress, 100)}%` }}
            ></div>
          </div>
          <div className="terminal-percentage">
            {Math.round(Math.min(displayProgress, 100))}%
          </div>
        </div>
      </div>

      {subtitle && <div className="gaming-subtitle">{subtitle}</div>}
    </div>
  );
};

export default GamingLoader;
